import { flash, FormGroup, Label, Modal, SubmitButton, Textarea } from "@xola/ui-kit";
import { DateFormat, formatDate, FormField, requiredString, SelectField } from "common";
import { Field } from "formik";
import React from "react";
import { useMutation, useQueryClient } from "react-query";
import { Except } from "type-fest";
import { PaymentMethod, PaymentMethodCopy } from "types";
import { object } from "yup";
import shallow from "zustand/shallow";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { usePartnerCommissionsFilterStore } from "../../../stores/reports/partnerCommissionsFilterStore";
import { Currency } from "../../Common/Currency";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";

// TODO: Rushi removed AffiliatePaymentMethod, but we should check if this adds unnecessary methods

export enum SettlementType {
    COLLECT = "Collect",
    PAY = "Pay",
}

export interface PaymentSettlementFormValues {
    comment: string;
    method: PaymentMethod;
}

const initialValues: PaymentSettlementFormValues = {
    comment: "",
    method: PaymentMethod.CHECK,
};

const validationSchema = object().shape({
    method: requiredString(),
});

export interface PaymentSettlementData {
    partnerId: ID;
    balanceDue: number;
    paymentSettlementType: SettlementType;
    children: boolean;
    purchases?: ID[];
}

interface PaymentSettlementModalProps {
    paymentSettlementData?: PaymentSettlementData;
    isOpen: boolean;
    onClose: () => void;
}

export const PaymentSettlementModal = ({ paymentSettlementData, isOpen, onClose }: PaymentSettlementModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent paymentSettlementData={paymentSettlementData} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ paymentSettlementData, onClose }: Except<PaymentSettlementModalProps, "isOpen">) => {
    const invalidate = useInvalidate();
    const maxCommentLength = 160;
    const [start, end] = usePartnerCommissionsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const amount = paymentSettlementData?.balanceDue ?? 0;
    const paymentSettlementType = paymentSettlementData?.paymentSettlementType ?? SettlementType.COLLECT;
    const selectedBookings = paymentSettlementData?.purchases?.length;
    const queryClient = useQueryClient();

    const { mutateAsync: updateTransaction, isLoading } = useMutation(
        async (formValues: PaymentSettlementFormValues) => {
            const amountToSettle = Math.abs(Math.round(amount * 100) / 100);
            const payload = {
                ...formValues,
                start: formatDate(start, DateFormat.DATE_ISO),
                end: formatDate(end, DateFormat.DATE_ISO),
                amount: amountToSettle,
                children: paymentSettlementData?.children,
                purchases: paymentSettlementData?.purchases,
            };
            return paymentSettlementType === SettlementType.COLLECT
                ? api.post(`partners/${paymentSettlementData?.partnerId}/collect`, payload)
                : api.post(`partners/${paymentSettlementData?.partnerId}/pay`, payload);
        },
    );

    const handleSubmit = async (values: PaymentSettlementFormValues) => {
        await updateTransaction(values);
        setTimeout(() => {
            queryClient.invalidateQueries("partnerCommissionsReport"); // Because is not a collection or has ID
            invalidate("affiliates");
            invalidate("partners");
            invalidate("transactions");
            invalidate("purchases");
            flash.success("Successfully updated transaction");
            onClose();
        }, 1000); // give some time for elastic search to get updated and invalidate to get new data
    };

    return (
        <XolaFormik
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={initialValues}
            onReset={onClose}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values }) => (
                <>
                    <Modal.Header>
                        {paymentSettlementType === SettlementType.COLLECT ? "Collect Payment" : "Pay Partner"}
                    </Modal.Header>

                    <Modal.Body className="flex-col">
                        <fieldset className="flex-1 space-y-4" disabled={isLoading}>
                            <div>
                                <Label>Period</Label>
                                <NoTranslate>
                                    {formatDate(start, DateFormat.DATE)} - {formatDate(end, DateFormat.DATE)}
                                </NoTranslate>
                            </div>

                            <div>
                                <Label>Amount</Label>
                                {amount && <Currency>{amount}</Currency>}{" "}
                                {paymentSettlementType === SettlementType.COLLECT
                                    ? "collected from partner"
                                    : "paid to partner"}
                            </div>

                            <div>
                                <Label>Scope</Label>
                                <h5>
                                    {selectedBookings ? (
                                        <>
                                            <var data-var="bookingsCount">{selectedBookings}</var> purchase selected
                                        </>
                                    ) : (
                                        "All unreconciled purchases"
                                    )}
                                </h5>
                            </div>

                            <FormField name="method" label="Method">
                                <PaymentMethods />
                            </FormField>

                            <FormGroup>
                                <Label>Comment</Label>
                                <Field maxLength={maxCommentLength} className="h-28" name="comment" as={Textarea} />
                                <NoTranslate className="float-right text-sm text-gray-darker">
                                    {values.comment.length}/{maxCommentLength}
                                </NoTranslate>
                            </FormGroup>
                        </fieldset>
                    </Modal.Body>

                    <Modal.Footer className="flex justify-end space-x-4">
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                        <SubmitButton type="submit" isLoading={isLoading} onClick={handleSubmit}>
                            Save
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

const PaymentMethods = () => {
    const seller = useSeller();
    const paymentMethodValues = getPaymentMethodsForPartnerSettlement();
    const isCreditLineEnabled = seller.preferences.affiliate.affiliateCreditLine.enabled;

    return (
        <SelectField>
            {paymentMethodValues.map((paymentMethodValue) => {
                let isEnabled = true;
                if (paymentMethodValue === PaymentMethod.CREDIT_LINE) {
                    isEnabled = isCreditLineEnabled;
                }

                return (
                    isEnabled && (
                        <option key={paymentMethodValue} value={paymentMethodValue}>
                            {PaymentMethodCopy[paymentMethodValue]}
                        </option>
                    )
                );
            })}
        </SelectField>
    );
};

const getPaymentMethodsForPartnerSettlement = () => {
    return [
        PaymentMethod.CHECK,
        PaymentMethod.CASH,
        PaymentMethod.ach, // as per V1
        PaymentMethod.WIRE,
        PaymentMethod.OTHER,
    ];
};
